import avatarSrc from "../../helpers/avatarSrc";
import React, { useState, useEffect, useRef, useContext } from "react";
import { useTranslation } from "react-i18next";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green, grey, red, blue, yellow } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Box from "@material-ui/core/Box";

import { i18n } from "../../translate/i18n";

import { toast } from "react-toastify";
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';
import CloseIcon from '@material-ui/icons/Close';
import { CircularProgress, IconButton, Tooltip, MenuItem, Menu } from "@material-ui/core";
import ContactTag from "../ContactTag";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import StarIcon from '@material-ui/icons/Star';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import StarOutlineIcon from '@material-ui/icons/StarOutline';
import TransformIcon from '@material-ui/icons/Transform';

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext, isMobileClient } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import RoomIcon from '@material-ui/icons/Room';
import AndroidIcon from "@material-ui/icons/Android";
import ChannelIcon from "../ChannelIcon";
import VisibilityIcon from "@material-ui/icons/Visibility";
import TicketMessagesDialog from "../TicketMessagesDialog";
import DoneIcon from '@material-ui/icons/Done';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";

const useStyles = makeStyles((theme) => ({
  // Subtle attention pulse for AI→Agent handoff badge — agents need
  // to notice these tickets quickly. Halo-style expand, not a scale
  // bounce (taste-conservative, doesn't get tiring). The halo lives
  // on a ::after pseudo-element and only animates opacity/transform —
  // compositor-only, so the infinite loop doesn't repaint the list
  // every frame like the old box-shadow keyframes did.
  "@keyframes handoffPulse": {
    "0%, 100%": { opacity: 0, transform: "scale(0.92)" },
    "50%": { opacity: 1, transform: "scale(1)" },
  },
  // Fade-in for newly-rendered list items (status switch, search,
  // socket arrival). Cheap on the GPU — opacity + tiny translateY.
  "@keyframes ticketFadeIn": {
    from: { opacity: 0, transform: "translateY(4px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  handoffBadge: {
    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    color: "#fff",
    fontSize: "8px",
    fontWeight: 700,
    padding: "2px 5px",
    borderRadius: 4,
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
    boxShadow: "0 1px 3px rgba(245, 158, 11, 0.3)",
    whiteSpace: "nowrap",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: 4,
      boxShadow: "0 0 0 4px rgba(245, 158, 11, 0.18)",
      pointerEvents: "none",
      animation: "$handoffPulse 2.4s ease-in-out infinite",
    },
    "@media (prefers-reduced-motion: reduce)": {
      "&::after": {
        animation: "none",
        opacity: 0,
      },
    },
  },
  ticket: {
    position: "relative",
    borderRadius: 12,
    margin: "6px 4px",
    padding: "12px 14px",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    animation: "$ticketFadeIn 0.22s ease-out",
    backgroundColor: theme.palette.type === "dark"
      ? "#131614"
      : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid #e2e8f0",
    boxShadow: theme.palette.type === "dark"
      ? "0 2px 4px rgba(0,0,0,0.2)"
      : "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(37, 211, 102, 0.10)"
        : "#f6faf7",
      borderColor: theme.palette.type === "dark"
        ? "rgba(37, 211, 102, 0.35)"
        : "#86efac",
      boxShadow: theme.palette.type === "dark"
        ? "0 4px 12px rgba(37, 211, 102, 0.22)"
        : "0 4px 12px rgba(37, 211, 102, 0.18)",
      transform: "translateY(-1px)",
    },
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(37, 211, 102, 0.18)"
        : "rgba(37, 211, 102, 0.10)",
      borderColor: theme.palette.type === "dark"
        ? "rgba(37, 211, 102, 0.55)"
        : "#25d366",
      borderWidth: "2px",
      boxShadow: theme.palette.type === "dark"
        ? "0 6px 16px rgba(37, 211, 102, 0.30)"
        : "0 6px 16px rgba(37, 211, 102, 0.22)",
    },
    [theme.breakpoints.down("sm")]: {
      margin: "4px 6px",
      padding: "10px 12px",
      borderRadius: 10,
    },
  },
  pendingTicket: {
    cursor: "unset",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },

  noTicketsText: {
    textAlign: "center",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.7)" : "#374151",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
  },

  lastMessageTime: {
    justifySelf: "flex-end",
    fontSize: "11px",
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.5)" : "#9ca3af",
  },

  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: 20,
    fontSize: 12,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280",
  },

  newMessagesCount: {
    alignSelf: "center",
    position: 'relative',
    top: -15,
    "& .MuiBadge-badge": {
      transition: "all 0.3s ease-in-out",
      background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
      boxShadow: "0 3px 10px rgba(37, 211, 102, 0.5)",
      color: "#fff",
    },
  },

  channelBadge: {
    position: 'relative',
    bottom: '-12px',
    left: theme.direction !== "rtl" ? '-25px' : '25px',
  },

  bottomButton: {
    position: "relative",
  },

  badgeStyle: {
    color: "white",
    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    transition: "transform 0.2s ease-in-out, opacity 0.2s ease-in-out",
    boxShadow: "0 2px 6px rgba(239, 68, 68, 0.4)",
  },

  acceptButton: {
    position: "absolute",
    left: "50%",
  },

  ticketQueueColor: {
    flex: "none",
    width: "4px",
    height: "calc(100% - 16px)",
    position: "absolute",
    top: "8px",
    left: "4px",
    borderRadius: 4,
  },
  userTag: {
    position: "absolute",
    marginRight: 5,
    right: 10,
    bottom: 30,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(46, 227, 122, 0.14)"
      : "rgba(37, 211, 102, 0.10)",
    color: theme.palette.type === "dark" ? "#7df0a8" : "#128c7e",
    border: "none",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: "0.75em",
    fontWeight: 600,
  },
  ticketInfo: {
    position: "relative",
    top: 0
  },

  ticketInfo1: {
    position: "relative",
    top: 40,
    right: 0
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 6,
      position: "inherit",
      height: 'auto',
      margin: 2,
      padding: '4px 8px',
      fontSize: '10px',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.3px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 'auto',
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },
  },
  presence: {
    color: theme.mode === 'light' ? "green" : "lightgreen",
    fontWeight: "bold",
  },
  secondaryContentSecond: {
    display: 'flex',
    marginTop: 2,
    //marginLeft: "5px",
    alignItems: "flex-start",
    flexWrap: "wrap",
    flexDirection: "row",
    alignContent: "flex-start",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  toolsContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  toolsIconButton: {
    padding: 0,
  },
  menuItemButton: {
    "& .MuiListItem-root": {
      fontSize: '14px',
      padding: '10px 16px',
      borderRadius: 8,
      margin: '2px 6px',
      transition: 'all 0.2s ease',
      "&:hover": {
        backgroundColor: theme.palette.type === "dark"
          ? "rgba(46, 227, 122, 0.12)"
          : "rgba(37, 211, 102, 0.08)",
      },
    },
    "& .MuiPaper-rounded": {
      borderRadius: 14,
      boxShadow: theme.palette.type === "dark"
        ? "0 8px 32px rgba(0,0,0,0.4)"
        : "0 8px 32px rgba(0,0,0,0.12)",
      border: theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.06)",
      backgroundColor: theme.palette.type === "dark"
        ? "#131614"
        : "#ffffff",
      padding: '4px 0',
      minWidth: 180,
    },
    "& .MuiMenuItem-root": {
      gap: 10,
    },
  },
  listItemContainer: {
    marginLeft: theme.direction === 'rtl' ? 0 : -15,
    marginRight: theme.direction === 'rtl' ? -15 : 0
  },
  channelLabel: {
    fontSize: "9px",
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: "6px",
    color: "#fff",
    marginLeft: "6px",
    display: "inline-block",
    verticalAlign: "middle",
    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
  }
}));

const TicketListItemCustom = ({ ticket, setTabOpen, setStatusParam

 }) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const [ticketUser, setTicketUser] = useState(null);
  const [whatsAppName, setWhatsAppName] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const { ticketId } = useParams();
  const [tag, setTag] = useState([]);

  const isMounted = useRef(true);
  const { setCurrentTicket, navigateToTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  const [isFavorite, setIsFavorite] = useState(ticket.isFav || false);
  const [ticketMenu, setTicketMenu] = useState(false);

  const { profile } = user;

  useEffect(() => {
    if (ticket.userId && ticket.user) {
      setTicketUser(ticket.user.name);
    }

    if (ticket.whatsappId && ticket.whatsapp) {
      setWhatsAppName(ticket.whatsapp.name);
    }
    if (ticket.tags) {
      setTag(ticket.tags)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  // Unmount-ONLY cleanup. This used to live in the [ticket] effect above,
  // so the FIRST socket update to the row flipped isMounted for good and
  // the transfer-modal post-await guard was permanently dead (the
  // "state update on unmounted component" warning in the console).
  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  const handleCloseTicket = async (id) => {
    try {
      await api.put(`/tickets/${id}`, {
        status: "closed",
        justClose: true,
        userId: user?.id,
      });
    } catch (err) {
      toastError(err);
    }
    setTicketMenu(false)
    history.push(`/tickets/`);
  };

  const handleToggleFavorite = async (id) => {
    try {
      const updatedTicket = await api.put(`/tickets/fav/${id}`, {
        isFav: !isFavorite,
      });
      if (updatedTicket) {
        toast.success(i18n.t("announcements.toasts.success"));
        setIsFavorite(!isFavorite);
      }
      setTicketMenu(false)

    } catch (err) {
      toastError(err);
    }
  };

  const handleAcceptTicket = async (id) => {
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
      });
    } catch (err) {
      // Lost the accept race (taken by another agent / 403): do NOT
      // deep-link into a chat we do not own - the toast explains it.
      toastError(err);
      setTicketMenu(false);
      return;
    }

    // Mobile: STAY on the Pending list - accepting is a batch flow (see
    // the comment below), and navigating flipped TicketsAdvanced into the
    // chat view after every accept; the old bare history.push also left a
    // ghost chat URL under the list. Desktop opens the accepted chat as
    // before, through the parity-aware navigator (code = call-site nonce).
    if (!isMobileClient()) {
      navigateToTicket({ id: ticket.id, uuid: ticket.uuid, code: uuidv4() });
    }
    setTicketMenu(false);
    // Don't force-switch the active tab. The just-accepted ticket
    // exits the Pending list via the socket update; the operator
    // usually wants to stay on Pending to keep working through the
    // queue rather than be ejected to Open after every accept.
  };

  const handleCloseTransferTicketModal = () => {
    if (isMounted.current) {
      setTransferTicketModalOpen(false);
      setTicketMenu(false)
    }
  };

  const handleOpenAcceptTicketWithouSelectQueue = () => {
    setTransferTicketModalOpen(true);
  };

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    // Use navigateToTicket (synchronous navigation) instead of setCurrentTicket
    // alone — the latter defers the history.push to a useEffect that runs after
    // the next render cycle, causing a 1-frame delay that made opening a ticket
    // feel like it required 2 clicks on busy lists.
    navigateToTicket({ id, uuid, code });
  };

  const badgeStyle = (bg, shadow) => ({
    background: bg,
    padding: "3px 8px",
    borderRadius: "5px",
    color: "white",
    boxShadow: shadow,
    fontSize: "9px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    lineHeight: 1.3,
  });

  // Was a chunky upper-case green pill identical to the contact-name
  // bubble — created visual "two people named X" confusion when the
  // assigned agent and contact shared a name. New treatment: a quiet
  // chip with a person-icon prefix that reads as "assigned to"
  // instead of competing with the contact identity.
  //
  // First pass used light-green-on-light-tint (rgba(125,240,168,0.10)
  // bg + #7df0a8 text) which Raviv flagged on 2026-05-19 — agent name
  // was nearly invisible on the light row background. Bumped to brand
  // deep green for the text + matching tint background + visible
  // border so it stays legible without looking like a status badge.
  const agentChipStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 7px",
    borderRadius: 999,
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    background: "rgba(18, 140, 126, 0.10)",
    color: "#0e6c61",
    border: "1px solid rgba(18, 140, 126, 0.28)",
  };

  const renderTicketInfo = () => {
    const queueColor = ticket.queue?.color || "#7C7C7C";
    return (
      <>
        {ticketUser && ticket.status !== "pending" && (
          <span style={agentChipStyle} title={`Assigned to ${ticketUser}`}>
            <PersonOutlineIcon style={{ fontSize: 11 }} />
            <span style={{ textTransform: "none" }}>{ticketUser}</span>
          </span>
        )}
        {ticket.queue?.name != null && (
          <span style={badgeStyle(
            `linear-gradient(135deg, ${queueColor} 0%, ${queueColor}dd 100%)`,
            `0 2px 6px ${queueColor}50`
          )}>
            {ticket.queue?.name || t("ticketsList.noQueue")}
          </span>
        )}
      </>
    );
  };


  return (
    <React.Fragment key={ticket.id}>
      <TicketMessagesDialog
        open={openTicketMessageDialog}
        handleClose={() => setOpenTicketMessageDialog(false)}
        ticketId={ticket.id}
      ></TicketMessagesDialog>
      <TransferTicketModalCustom
        modalOpen={transferTicketModalOpen}
        onClose={handleCloseTransferTicketModal}
        ticketid={ticket.id}
        uuid={ticket.uuid}
        setStatusParam={setStatusParam}
        user={user}
      />
     <ListItem
        dense
        selected={ticketId && +ticketId === ticket.id}
        className={clsx(classes.ticket, {
          [classes.pendingTicket]: ticket.status === "pending",
        })}
      >
         {/* Apple/Raviv 2026-06-07: removed `ticket.lastMessage &&` wrapper.
             It hid the badge for any ticket without a lastMessage preview
             (common after move from Pending → Custom tab where lastMessage
             can be empty before the next socket update). Material-UI Badge
             already self-hides when badgeContent is 0 / undefined, so the
             gate was both unnecessary and harmful. */}
        <Badge
          className={classes.newMessagesCount}
          badgeContent={ticket.unreadMessages}
          overlap="rectangular"
          max={9999}
          classes={{
            badge: classes.badgeStyle,
          }}
        />
         <ListItemAvatar
         onClick={(e) => {
          handleSelectTicket(ticket);
        }}>
          <Avatar style={{ backgroundColor: generateColor(ticket?.contact?.number), color: "white", fontWeight: "bold" }} imgProps={{ loading: "lazy" }} src={avatarSrc(ticket?.contact?.profilePicUrl)}>{ getInitials(ticket?.contact?.name || "") }</Avatar>
        </ListItemAvatar>
        <ListItemText
         className={classes.listItemContainer}
         onClick={(e) => {
          handleSelectTicket(ticket);
        }}
          disableTypography
          primary={
            <span style={{ display: "flex", alignItems: "center", flexWrap: "nowrap", justifyContent: "space-between", width: "100%" }}>
              <span style={{ display: "flex", alignItems: "center", flexWrap: "wrap", flex: 1, minWidth: 0, gap: 4 }}>
                {ticket.whatsapp?.channelLabel && (
                  <span
                    className={classes.channelLabel}
                    style={{
                      backgroundColor: ticket.whatsapp?.channelLabelColor || "#FF9800",
                      marginLeft: 0,
                      marginRight: "4px"
                    }}
                  >
                    {ticket.whatsapp?.channelLabel}
                  </span>
                )}
                <Typography
                  noWrap
                  component="span"
                  variant="body2"
                  color="textPrimary"
                  style={{
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {ticket.contact?.name}
                </Typography>
                {/* Phase 3.3 — Reference ID (WA-000123 / CU-000123) */}
                {ticket.contact?.displayId && (
                  <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: "#94a3b8",
                    letterSpacing: "0.03em",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}>
                    {ticket.contact.displayId}
                  </span>
                )}
              </span>
            </span>
          }
          secondary={
            <span style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography
                className={classes.contactLastMessage}
                noWrap
                component="span"
                variant="body2"
                color="textSecondary"
              >
                {ticket.lastMessage ? (
                  <MarkdownWrapper>{(([...ticket.lastMessage].slice(0, 30).join("")) + ([...ticket.lastMessage].length > 30 ? " ..." : ""))}</MarkdownWrapper>
                ) : (
                  " "
                )}
              </Typography>
              <span style={{ display: "flex", flexWrap: "wrap", gap: "3px", alignItems: "center", overflow: "hidden", maxWidth: "100%" }}>
                {renderTicketInfo()}
              </span>
              {tag?.length > 0 && (
                <span style={{ display: "flex", flexWrap: "wrap", gap: "3px", alignItems: "center", overflow: "hidden", maxWidth: "100%" }}>
                  {tag.map((t) => (
                    <ContactTag tag={t} key={`ticket-contact-tag-${ticket.id}-${t.id}`} />
                  ))}
                </span>
              )}
            </span>
          }
        />

        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 45, marginLeft: 4, flexShrink: 0 }}>
          {/* Channel icon — above timestamp, natural system-language placement */}
          {ticket.channel && (
            <Tooltip
              arrow
              title={i18n.t(
                `channelFilter.${
                  ticket.channel === "whatsapp-api"
                    ? "whatsapp"
                    : ticket.channel === "business-api"
                    ? "businessApi"
                    : ticket.channel
                }`
              )}
            >
              <span style={{ display: "inline-flex", marginBottom: 3 }}>
                <ChannelIcon channel={ticket.channel} size={18} style={{ flexShrink: 0 }} />
              </span>
            </Tooltip>
          )}
          {/* Timestamp on every ticket, not just WhatsApp ones. Raviv
              flagged the old `ticket.whatsappId && ...` gate as a bug —
              Instagram / Facebook / other channels still have an
              updatedAt and the timestamp is a baseline list affordance.
              The whatsappId-gated guard pre-dated multi-channel and
              left non-WhatsApp tickets visually undated. */}
          {ticket.updatedAt && (
            <Typography
              component="span"
              variant="body2"
              color="textSecondary"
              style={{
                whiteSpace: "nowrap",
                fontSize: '10px',
              }}
            >
              {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                format(parseISO(ticket.updatedAt), "HH:mm")
              ) : (
                format(parseISO(ticket.updatedAt), "dd/MM/yyyy")
              )}
            </Typography>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: "3px", marginTop: "2px", flexWrap: "nowrap", justifyContent: "flex-end" }}>
            {ticket.chatbot && (
              <Tooltip title="Chatbot">
                <AndroidIcon style={{ color: '#7dd42d', fontSize: '14px' }} />
              </Tooltip>
            )}
            {ticket.aiHandoff && (
              <Tooltip title={`AI Handoff: ${ticket.aiHandoffReason || 'Passed to agent'}`}>
                <span className={classes.handoffBadge}>
                  <TransformIcon style={{ fontSize: '10px' }} />
                  AI→Agent
                </span>
              </Tooltip>
            )}
            {ticket.disableAI && !ticket.aiHandoff && (
              <Tooltip title="AI Disabled">
                <span style={{
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: '#fff',
                  fontSize: '8px',
                  fontWeight: 700,
                  padding: '2px 5px',
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(107, 114, 128, 0.3)',
                  whiteSpace: 'nowrap',
                }}>
                  AI OFF
                </span>
              </Tooltip>
            )}
            {isFavorite && (
              <StarIcon style={{ fontSize: '14px', color: yellow[700] }} />
            )}
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <IconButton className={classes.toolsIconButton} onClick={(event) => {
              setAnchorEl(event.currentTarget);
              setTicketMenu(true)
            }}>
              <ExpandMoreIcon />
            </IconButton>
              <Menu
                className={classes.menuItemButton}
                id="menu-appbar"
                anchorEl={anchorEl}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={ticketMenu}
                onClose={() => {
                  setAnchorEl(null);
                  setTicketMenu(false)
                }}
              >
                {ticket.status === "pending" && (
                  <MenuItem onClick={() => {
                    handleAcceptTicket(ticket.id)
                  }}>
                    <AddBoxOutlinedIcon style={{
                      fontSize: '16px',
                      marginRight: 10
                    }} />
                    <Typography>
                    {i18n.t("ticketsList.buttons.accept")}
                    </Typography>
                  </MenuItem>

                )}
                {(ticket.status === "open" || ticket.status === 'pending') && (
                  <MenuItem onClick={() => {
                    handleCloseTicket(ticket.id)
                  }}>
                    <CloseIcon style={{
                      fontSize: '16px',
                      marginRight: 10
                    }} />
                    <Typography>
                    {i18n.t("tickets.tabs.closed.title")}
                    </Typography>

                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => handleToggleFavorite(ticket.id)}
                >
                  {isFavorite ? (
                    <StarIcon style={{
                      fontSize: '16px',
                      marginRight: 10
                    }} />) : (
                    <StarOutlineIcon style={{
                      fontSize: '16px',
                      marginRight: 10
                    }} />
                  )}
                  <Typography>
                  {i18n.t("quickMessages.buttons.save")}
                  </Typography>

                </MenuItem>
                <MenuItem
                  onClick={e => handleOpenAcceptTicketWithouSelectQueue()
                  }>
                  <TransformIcon style={{
                    fontSize: '16px',
                    marginRight: 10
                  }} />
                  <Typography>
                  {i18n.t("transferTicketModal.buttons.ok")}

                  </Typography>
                </MenuItem>
              </Menu>
            </div>
        </div>
      </ListItem>
    </React.Fragment>
  );
};

// React.memo: prevents every ticket in the list from re-rendering when
// an unrelated ticket receives a socket event. Without this, any socket
// UPDATE_TICKET returns a new array reference from the reducer, causing
// the entire list to re-render — each item gets its fade-in animation
// replayed, producing the "transparent/flashing" effect on new messages.
export default React.memo(TicketListItemCustom);
